/**
 * chatDeepLink.ts — /line を URL で特定の会話を開いた状態にする（2026-10-31・booking-platform/md/customer-chat.md §13-8）
 *
 * 未返信チャットの通知メール（LP の notify_sweep）は `https://<店舗の r-cal>/line?chat=<session_key>&store=<naha|miyako>` を載せる。
 *
 * - `chat=res_…`（予約チャット）/ `chat=user_…`（匿名チャット）/ `chat=line:<LINE ユーザー ID>`（LINE）
 * - `store` は会話がある店舗（LP の店舗キー naha / miyako）。この画面の店舗と違えば開かずに案内だけ出す。省略可
 * - 顧客の名前・メール・電話は URL に入れない（session_key だけ。従来のメールのリンクにも出ていた）
 * - 開いたら（見つからなくても）パラメータを URL から消す。再読み込みで開き直さない
 * - ログイン前に開いたときは、App がログイン画面を同じ URL のまま出すので、ログイン後に /line が描かれた時点で開く
 */
import { RESV_INSTANCE_PREFIX } from './reservationChat';

export const CHAT_DEEP_LINK_PARAM = 'chat';
export const CHAT_DEEP_LINK_STORE_PARAM = 'store';
export const LINE_DEEP_LINK_PREFIX = 'line:';

export type ChatDeepLinkStore = 'naha' | 'miyako';
export type ChatDeepLinkTarget =
  | { kind: 'lp'; sessionKey: string }
  | { kind: 'line'; lineUserId: string };
export interface ChatDeepLink {
  target: ChatDeepLinkTarget | null; // 形式がおかしいときは null（案内だけ出して消す）
  store: ChatDeepLinkStore | null;
}

const LP_KEY_RE = /^(res|user)_[A-Za-z0-9_-]{1,128}$/;
const LINE_ID_RE = /^U[0-9A-Za-z]{8,64}$/;

/** URL の検索文字列から読む。パラメータが無ければ null */
export function parseChatDeepLink(search: string): ChatDeepLink | null {
  const params = new URLSearchParams(search);
  if (!params.has(CHAT_DEEP_LINK_PARAM)) return null;
  const raw = (params.get(CHAT_DEEP_LINK_PARAM) || '').trim();
  const s = (params.get(CHAT_DEEP_LINK_STORE_PARAM) || '').trim().toLowerCase();
  const store: ChatDeepLinkStore | null = s === 'naha' || s === 'miyako' ? s : null;
  let target: ChatDeepLinkTarget | null = null;
  if (raw.startsWith(LINE_DEEP_LINK_PREFIX)) {
    const id = raw.slice(LINE_DEEP_LINK_PREFIX.length);
    if (LINE_ID_RE.test(id)) target = { kind: 'line', lineUserId: id };
  } else if (LP_KEY_RE.test(raw)) {
    target = { kind: 'lp', sessionKey: raw };
  }
  return { target, store };
}

/** chat / store を除いた検索文字列（先頭の ? 付き。空なら ''） */
export function stripChatDeepLink(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(CHAT_DEEP_LINK_PARAM);
  params.delete(CHAT_DEEP_LINK_STORE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

export type ChatDeepLinkResult =
  | { status: 'open'; instanceKey: string }
  | { status: 'invalid' | 'other_store' | 'not_found' | 'disabled' };

export interface ChatDeepLinkContext {
  ownStore: ChatDeepLinkStore;
  lpEnabled: boolean;
  lineEnabled: boolean;
  /** LP の会話一覧（get_sessions。アーカイブ済みも含む） */
  sessions: Array<{ session_key: string }>;
  /** /line のカード（LINE は予約ごとに 1 枚。instanceKey と line_user_id を持つ） */
  instances: Array<{ instanceKey: string; line_user_id?: string; unread_count?: number }>;
}

/**
 * 開くカードを決める。読み込みが終わってから呼ぶ（呼ぶ側が待つ）。
 * 予約チャットが LINE のカードにまとめられていても `resv:<session_key>` を開けば、
 * パネルは予約チャット側を表示し「同じ予約の会話」で LINE に切り替えられる（LineChatBoard のパネル）。
 */
export function resolveChatDeepLink(link: ChatDeepLink, ctx: ChatDeepLinkContext): ChatDeepLinkResult {
  if (!link.target) return { status: 'invalid' };
  if (link.store && link.store !== ctx.ownStore) return { status: 'other_store' };
  if (link.target.kind === 'lp') {
    if (!ctx.lpEnabled) return { status: 'disabled' };
    const sk = link.target.sessionKey;
    return ctx.sessions.some((s) => s.session_key === sk)
      ? { status: 'open', instanceKey: `${RESV_INSTANCE_PREFIX}${sk}` }
      : { status: 'not_found' };
  }
  if (!ctx.lineEnabled) return { status: 'disabled' };
  const uid = link.target.lineUserId;
  const mine = ctx.instances.filter((i) => i.line_user_id === uid && !String(i.instanceKey).startsWith(RESV_INSTANCE_PREFIX));
  if (mine.length === 0) return { status: 'not_found' };
  const pick = mine.find((i) => (i.unread_count || 0) > 0) || mine[0];
  return { status: 'open', instanceKey: pick.instanceKey };
}

const STORE_LABEL: Record<ChatDeepLinkStore, string> = { naha: '那覇', miyako: '宮古' };

/** 開けなかったときの短い案内（一覧はそのまま） */
export function chatDeepLinkNotice(result: ChatDeepLinkResult, link: ChatDeepLink): string | null {
  switch (result.status) {
    case 'open':
      return null;
    case 'other_store':
      return `この会話は${link.store ? STORE_LABEL[link.store] : '別の'}店の会話です。${link.store ? STORE_LABEL[link.store] : 'その'}店の予約管理の LINE ページで開いてください`;
    case 'disabled':
      return 'この店舗ではこの種類のチャットを使っていません。一覧を表示しています';
    case 'invalid':
      return 'リンクの形式が正しくないため、会話を開けませんでした。一覧を表示しています';
    default:
      return 'リンクの会話が見つかりませんでした（期間外・削除済みの可能性があります）。一覧を表示しています';
  }
}
